import format from 'date-fns/format';
import enUsLocale from 'date-fns/locale/en-US';

import { isValidNumber } from './miscHelpers';

/**
 * In the future we may need multiple languages we can change the displayLocale variable to change the language
 * for example: change the date text to Spanish
 * import esLocale from 'date-fns/locale/es';
 * check the current language selection.
 * If the language is Spanish, set displayLocale to esLocale. The output date string will be Spanish
 */
const displayLocale = enUsLocale;

export const epochToTimeInReadableFormat = (epochTime) => {
  if (epochTime == null || epochTime === '') {
    return '';
  }

  const parsedEpochTime = parseInt(epochTime, 10);

  if (Number.isNaN(parsedEpochTime)) {
    return '';
  }

  const date = new Date(parsedEpochTime);

  if (Number.isNaN(date.getTime())) {
    return '';
  }

  // Get the local time, considering daylight saving time
  // returns format  => "01:00 PM"
  return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
};

export const epochToDateInReadableFormat = (epochTime) => {
  if (epochTime == null || epochTime === '') {
    return '';
  }

  const parsedEpochTime = parseInt(epochTime, 10);

  if (Number.isNaN(parsedEpochTime)) {
    return '';
  }

  const d = new Date(parsedEpochTime);

  if (Number.isNaN(d.getTime())) {
    return '';
  }

  return d.toLocaleDateString();
};

export const isValidDateInstance = (date) => date instanceof Date && !Number.isNaN(date.getTime());

/**
 * Parses a `YYYY-MM-DD` calendar-date string (the format most APIs use for
 * date-only fields, e.g. document expiration ranges) into a `Date` set to
 * local midnight.
 *
 * We can't just do `new Date(dateString)` here:
 *  - Per the ES spec, a date-only ISO string (no time/offset) is parsed as
 *    UTC midnight, not local midnight. Reading it back with local getters
 *    (`getDate()`, `getMonth()`, ...) can then show the previous day for any
 *    timezone behind UTC (e.g. all US timezones), a classic off-by-one bug.
 *  - `Date.UTC`/the native parser also silently "rolls over" invalid
 *    calendar dates instead of rejecting them, e.g. `new Date('2026-02-31')`
 *    quietly becomes March 3rd rather than an Invalid Date. We want bad
 *    input from an API to fail loudly (return `null`) instead of shifting.
 *
 * @param {string} dateString - Calendar date in `YYYY-MM-DD` format.
 * @returns {Date|null} Local-midnight `Date`, or `null` if not a valid `YYYY-MM-DD` calendar date.
 */
export const parseDateOnlyString = (dateString) => {
  if (!dateString || typeof dateString !== 'string') {
    return null;
  }

  const dateMatch = dateString.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!dateMatch) {
    return null;
  }

  const [, year, month, day] = dateMatch;
  const yearNum = parseInt(year, 10);
  const monthNum = parseInt(month, 10) - 1;
  const dayNum = parseInt(day, 10);

  if (monthNum < 0 || monthNum > 11) {
    return null;
  }
  if (dayNum < 1 || dayNum > 31) {
    return null;
  }

  const date = new Date(yearNum, monthNum, dayNum);

  if (date.getFullYear() !== yearNum || date.getMonth() !== monthNum || date.getDate() !== dayNum) {
    return null;
  }

  return date;
};

/**
 * Formats a `Date` back into the `YYYY-MM-DD` calendar-date string APIs
 * expect, using local date components (the inverse of `parseDateOnlyString`).
 *
 * @param {Date} date
 * @returns {string} `YYYY-MM-DD`, or `''` if `date` isn't a valid `Date`.
 */
export const formatDateOnlyString = (date) => {
  if (!isValidDateInstance(date)) return '';

  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');

  return `${year}-${month}-${day}`;
};

export const weekDays = [
  'Sunday',
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
];

/**
 * Get the Sunday (start) of the week containing the given date.
 * Returned Date is normalized to local midnight.
 *
 * @param {Date|string|number} date
 * @returns {Date}
 */
export const getSundayOfWeek = (date) => {
  const sunday = new Date(date);
  const dayOfWeek = sunday.getDay(); // 0 = Sunday, 1 = Monday, ..., 6 = Saturday
  sunday.setDate(sunday.getDate() - dayOfWeek);
  sunday.setHours(0, 0, 0, 0);
  return sunday;
};

export const epochToDayOfWeek = (epoch) => {
  const dateObject = new Date(epoch);
  const dayIndex = dateObject.getDay();
  return weekDays[dayIndex];
};

/**
 * Strips the Hours and Minutes out of a JavaScript Date object
 * and returns the total number of minutes since midnight.
 * @param {Date} time
 * @returns int
 */
export const timeToMinutesFromMidnight = (time) => time.getHours() * 60 + time.getMinutes();

/**
 * Function that reformat javascript Date object
 * @param    {Date} date  Javascript Date Object contains date information
 * @return   {String}     More readable date string like "February 12, 2022"
 */
export const reformatDateMMMMDDYYYY = (date) => {
  try {
    return format(new Date(date), 'MMMM dd, yyyy', {
      locale: displayLocale,
    });
  } catch (e) {
    return '';
  }
};

export const minutesFromMidnightToTime = (minutes) => {
  const hours = minutes / 60;
  const flooredHours = Math.floor(minutes / 60);
  const remainderMinutes = (hours - flooredHours) * 60;
  const roundedMinutes = Math.round(remainderMinutes);

  const newDate = new Date();

  newDate.setHours(flooredHours);
  newDate.setMinutes(roundedMinutes);
  return newDate;
};

export const minutesFromMidnightToReadableTime = (minutes) => {
  const hours = minutes / 60;
  let flooredHours = Math.floor(minutes / 60);

  const remainderMinutes = (hours - flooredHours) * 60;

  // Determine AM or PM
  const ampm = flooredHours >= 12 ? 'pm' : 'am';

  const minutesToDisplay =
    remainderMinutes <= 9 ? `0${Math.round(remainderMinutes)}` : Math.round(remainderMinutes);

  if (flooredHours > 12) {
    flooredHours -= 12;
  }
  // Combine everything into the final string
  return `${flooredHours === 0 ? 12 : flooredHours}:${minutesToDisplay} ${ampm}`;
};

// returned min values is Minute
export const combineDateAndTime = (date, time) =>
  new Date(date.getFullYear(), date.getMonth(), date.getDate(), time.getHours(), time.getMinutes());

/**
 * Function that reformat javascript Date object
 * @param    {Date} date  Javascript Date Object Sat Sep 14 2024 00:00:00 GMT-0400 (Eastern Daylight Time)
 * @return   {String}     More readable date string like "02/12/2022"
 */

export const reformatDateToDDMMYYY = (date) => {
  if (date instanceof Date && !Number.isNaN(date.getTime())) {
    const month = date.getUTCMonth() + 1;
    const day = date.getUTCDate();
    const year = date.getUTCFullYear();
    return `${month?.toString()?.padStart(2, '0')}/${day?.toString()?.padStart(2, '0')}/${year}`;
  }
  return 'N/A';
};

export const capitalizeToShortWeekday = (abbr) => {
  if (typeof abbr === 'string') {
    const weekdayMapping = {
      monday: 'Mon',
      tuesday: 'Tue',
      wednesday: 'Wed',
      thursday: 'Thu',
      friday: 'Fri',
      saturday: 'Sat',
      sunday: 'Sun',
    };
    // Convert the abbreviation to the full Capitalized Abbreviated weekday name
    return weekdayMapping[abbr.toLowerCase()] || 'N/A';
  }
  return 'N/A';
};

export const getHoursDifferenceFromNow = (epochTime) => {
  if (Number.isNaN(epochTime) || typeof epochTime === 'string' || epochTime === undefined) {
    return `Err`;
  }
  const now = new Date();

  // Get the current time in milliseconds (UTC)
  const currentTimeUTC = now.getTime();

  // Return 0 if epochTime is in the past
  if (epochTime < currentTimeUTC) {
    return 0;
  }

  // Calculate the time difference in milliseconds
  const timeDiff = epochTime - currentTimeUTC;

  // Convert the time difference to hours
  const timeDiffInHours = timeDiff / (1000 * 60 * 60);

  return Math.floor(timeDiffInHours);
};

/**
 * Returns the IANA timezone of the user's browser (e.g. "America/New_York").
 * This is the only place that couples timezone resolution to the user's
 * physical location. Isolating it here makes the rest of the TZ logic testable.
 *
 * @returns {string} IANA timezone identifier
 */
export const getBrowserTimezone = () => Intl.DateTimeFormat().resolvedOptions().timeZone;

/**
 * Converts an epoch timestamp into a normalized timezone abbreviation for a
 * given IANA timezone. US timezones are collapsed to generic form
 * (EST/EDT -> ET, CST/CDT -> CT, MST/MDT -> MT, PST/PDT -> PT).
 *
 * @param {number|string} epochTime  Timestamp in milliseconds
 * @param {string} timeZone          IANA timezone (e.g. "America/New_York")
 * @returns {string}  Abbreviated timezone such as "ET", or the raw abbreviation
 *                    for non-US timezones (e.g. "GMT", "UTC").
 */
export const formatTimezoneAbbr = (epochTime, timeZone) => {
  if (epochTime == null || epochTime === '') return '';
  if (!timeZone) return '';

  const parsedEpochTime = parseInt(epochTime, 10);
  if (Number.isNaN(parsedEpochTime)) return '';

  const date = new Date(parsedEpochTime);
  if (Number.isNaN(date.getTime())) return '';

  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone,
    timeZoneName: 'short',
  }).formatToParts(date);

  const tzPart = parts.find((p) => p.type === 'timeZoneName');
  if (!tzPart) return '';

  const abbr = tzPart.value;

  const US_TZ_PREFIXES = 'ECMP';
  if (
    abbr.length === 3 &&
    US_TZ_PREFIXES.includes(abbr[0]) &&
    (abbr[1] === 'S' || abbr[1] === 'D') &&
    abbr[2] === 'T'
  ) {
    return abbr[0] + abbr[2];
  }

  return abbr;
};

/**
 * Convenience wrapper: resolves the timezone abbreviation for an epoch using
 * the browser's local timezone. Delegates to formatTimezoneAbbr.
 *
 * @param {number|string} epochTime  Timestamp in milliseconds
 * @returns {string}  Abbreviated timezone
 */
export const epochToTimezoneAbbr = (epochTime) =>
  formatTimezoneAbbr(epochTime, getBrowserTimezone());

export const millisecondsToHours = (milliseconds) => {
  if (isValidNumber(milliseconds)) {
    return milliseconds / 1000 / 60 / 60;
  }
  throw new Error('Invalid value, this function requires an epoch');
};

/**
 * Converts a future epoch (ms) into a countdown object.
 *
 * @param {number} epoch  Target timestamp in milliseconds
 * @returns {null | { expired: true } | { expired: false, display: string }}
 *   - `null` when the input is missing or not a valid number
 *   - `{ expired: true }` when the epoch is in the past (or exactly now)
 *   - `{ expired: false, display: "28d 16h 22m" }` for future epochs
 */
export const epochToCountdown = (epoch) => {
  if (epoch == null || typeof epoch !== 'number' || Number.isNaN(epoch)) {
    return null;
  }

  const now = Date.now();
  const diff = epoch - now;

  if (diff <= 0) {
    return { expired: true };
  }

  const totalMinutes = Math.floor(diff / (1000 * 60));
  const days = Math.floor(totalMinutes / (60 * 24));
  const hours = Math.floor((totalMinutes % (60 * 24)) / 60);
  const minutes = totalMinutes % 60;

  return { expired: false, display: `${days}d ${hours}h ${minutes}m` };
};
